import React from "react";
import { Box, type BoxProps, Flex, Heading, Spacer, Text } from "@chakra-ui/react";

export interface SectionCardProps extends BoxProps {
  /** Folio number, e.g. "01". */
  folio?: string;
  title: string;
  /** Short lede next to the title (body.sm muted). */
  lede?: string;
  /** Search / filters / segmented control / CTA — right-aligned on md+, full width beneath the title on base. */
  toolbar?: React.ReactNode;
  /** Sets aria-busy on the section while its skeleton is showing. */
  isLoading?: boolean;
  children: React.ReactNode;
}

/**
 * White section card with a folio-numbered header, a hairline rule with a 40px green tab, and a toolbar slot.
 * Replaces every borderWidth="2px" box + UPPERCASE heading. Content sits directly on the card (no inner grey boxes).
 * @example
 * <SectionCard folio="02" title="Stock analysis" lede="Estimated from your last 30 days of orders" toolbar={<SearchField … />} isLoading={isPending}>
 *   {isPending ? <TableSkeleton columns={cols} /> : <DataTable>…</DataTable>}
 * </SectionCard>
 */
export const SectionCard: React.FC<SectionCardProps> = ({ folio, title, lede, toolbar, isLoading, children, ...rest }) => (
  <Box as="section" layerStyle="card" p={{ base: 4, md: 6 }} aria-busy={isLoading ? "true" : undefined} minW={0} {...rest}>
    <Flex direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "baseline" }} gap={3}>
      <Flex align="baseline" gap={3} wrap="wrap" minW={0}>
        {folio && (
          <Text as="span" textStyle="label" color="leaf.700" minW="1.5rem" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {folio}
          </Text>
        )}
        <Heading as="h2" size="md">
          {title}
        </Heading>
        {lede && (
          <Text textStyle="body.sm" color="text.muted">
            {lede}
          </Text>
        )}
      </Flex>
      <Spacer />
      {toolbar && (
        <Flex gap={3} wrap="wrap" align="center" w={{ base: "100%", md: "auto" }} flexShrink={0}>
          {toolbar}
        </Flex>
      )}
    </Flex>
    <Box
      h="1px"
      bg="border.rule"
      my={5}
      position="relative"
      _after={{
        content: '""',
        position: "absolute",
        left: 0,
        top: "-1px",
        w: "40px",
        h: "3px",
        bg: "leaf.500",
        borderRadius: "full",
      }}
      aria-hidden
    />
    {children}
  </Box>
);
